import AdminLayout from "../../components/admin/AdminLayout";

const deviceChecks = [
  ["320px phones", "No horizontal overflow, readable headings, 44px touch targets"],
  ["375px/414px phones", "Cards stack cleanly and menus remain usable"],
  ["768px tablets", "Two-column layouts, comfortable spacing, scrollable data tables"],
  ["1024px tablets/laptops", "Admin shell adapts without hiding key actions"],
  ["1440px desktop", "Content remains centered with strong visual hierarchy"],
];

const tokens = [
  ["Primary", "#047857", "Buttons, active states, important institutional actions"],
  ["Deep green", "#064e3b", "Headers, strong banners, footer accents"],
  ["Gold", "#f59e0b", "Focus rings, highlights, calls to action"],
  ["Ink", "#0f172a", "Main text and headings"],
  ["Muted", "#64748b", "Descriptions and secondary text"],
];

export default function AdminUiUxCenter() {
  return (
    <AdminLayout
      title="UI/UX & Responsiveness Center"
      subtitle="Design-system guide and responsive quality checklist for all website pages and CMS screens."
    >
      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-emerald-700">Phase 13</p>
          <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">Responsive design system added</h2>
          <p className="mt-3 max-w-3xl text-slate-600 leading-7">
            This project now has global responsive utilities, safer mobile admin navigation, table overflow protection,
            improved touch targets, focus states, reduced-motion support, print-friendly rules, and reusable FPB design tokens.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {deviceChecks.map(([title, desc]) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <h3 className="font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl bg-emerald-950 p-6 text-white shadow-sm">
          <h2 className="text-2xl font-black">Recommended device test list</h2>
          <ol className="mt-5 space-y-3 text-sm leading-6 text-emerald-50">
            <li>1. Chrome DevTools: 320×568, 375×667, 414×896.</li>
            <li>2. Tablet portrait and landscape: 768×1024 and 1024×768.</li>
            <li>3. Laptop: 1366×768.</li>
            <li>4. Desktop: 1440×900 and 1920×1080.</li>
            <li>5. Keyboard-only navigation: Tab, Shift+Tab, Enter, Escape.</li>
            <li>6. Lighthouse check for Accessibility, SEO, Best Practices and Performance.</li>
          </ol>
        </section>
      </div>

      <section className="mt-6 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <h2 className="text-2xl font-black text-slate-950">FPB design tokens</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {tokens.map(([name, hex, usage]) => (
            <div key={name} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="h-20" style={{ backgroundColor: hex }} />
              <div className="p-4">
                <h3 className="font-black text-slate-900">{name}</h3>
                <p className="mt-1 font-mono text-sm text-slate-500">{hex}</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">{usage}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-xl font-black text-slate-950">Mobile UX</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li>✅ Admin drawer works on phones</li>
            <li>✅ Tables scroll horizontally</li>
            <li>✅ Touch targets improved</li>
            <li>✅ Text wraps safely</li>
          </ul>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-xl font-black text-slate-950">Accessibility UX</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li>✅ Focus rings improved</li>
            <li>✅ Reduced motion respected</li>
            <li>✅ External links hardened</li>
            <li>✅ Icon buttons get fallback labels</li>
          </ul>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-xl font-black text-slate-950">Production UX</h3>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li>✅ Print-friendly pages</li>
            <li>✅ Safe-area support</li>
            <li>✅ Better containers and spacing</li>
            <li>✅ Global no-overflow rules</li>
          </ul>
        </div>
      </section>
    </AdminLayout>
  );
}
